import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";
import { api } from "../lib/api";

function fmtErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const GENERIC = "If an account exists for that email, a password reset link has been sent.";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error(fmtErr(err.response?.data?.detail) || "Too many reset requests. Please try again in a few minutes.");
      } else {
        // Never reveal whether an account exists — show the same generic screen.
        setSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0C10] relative z-10">
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "linear-gradient(120deg, rgba(10,12,16,0.4), rgba(10,12,16,0.9)), url('https://images.unsplash.com/photo-1731239027673-1a5c6cd61380?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')",
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="absolute bottom-12 left-12 right-12">
          <div className="eyebrow text-[#D4AF37]">The Passport Experience™</div>
          <h1 className="font-display text-5xl text-[#F8FAFC] mt-3 leading-tight">
            Regain access to<br />your command centre.
          </h1>
          <p className="text-[#94A3B8] mt-4 max-w-md">
            We'll email a secure, single-use link so you can set a new private password and get straight back to Thailand 2027.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <img src="/inforno-logo.png" alt="Inforno Travel" className="h-11 w-auto" />
            <span className="font-display text-2xl">Inforno Travel</span>
          </div>

          {sent ? (
            <div data-testid="forgot-sent-panel">
              <div className="flex items-start gap-3 mb-4 p-4 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                <MailCheck className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
                <div className="text-sm text-[#F8FAFC]" data-testid="forgot-sent-message">{GENERIC}</div>
              </div>
              <p className="text-sm text-[#94A3B8] mb-6">
                Check your inbox for the reset link. It expires in 15 minutes and can be used once.
              </p>
              <Link to="/login" data-testid="forgot-back-login"
                className="gold-btn w-full py-3 rounded-lg inline-flex items-center justify-center">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="forgot-form">
              <h2 className="text-xl font-semibold mb-1">Forgot your password?</h2>
              <p className="text-sm text-[#94A3B8] mb-6">Enter your account email and we'll send a reset link.</p>
              <label className="block text-sm text-[#94A3B8] mb-1">Email</label>
              <input
                data-testid="forgot-email"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full mb-6 px-4 py-3 rounded-lg bg-[#12151E] border border-[#2A3042] focus:border-[#D4AF37] outline-none text-[#F8FAFC]"
              />
              <button
                data-testid="forgot-submit"
                type="submit" disabled={loading}
                className="gold-btn w-full py-3 rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {loading ? "Sending…" : "Send reset link"}
              </button>
              <div className="mt-6 text-center">
                <Link to="/login" data-testid="forgot-back-login-inline"
                  className="text-sm text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
                  ← Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
